import { Entity } from '@api';
import { useNewsFilters } from '@terminal/react-query-hooks/news/useNewsFilter';
import { NewsFilter } from '@terminal/services/news/news.types';
import { PosthogTrackingSource } from '@terminal/services/tracking/posthog/constants';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import { useWatchlist } from '@terminal/stores/use-watchlist/useWatchlist';
import { useNewsUrlStore } from '@terminal/views/news/stores/use-news-url-store/useNewsUrlStore';
import {
  applySelectionMapping,
  countFilterKeyChecked,
  createAccordionFilters,
  toggleSelectionMapping,
} from '@terminal/views/news/utils/news-filters-utils/news-filters-utils';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import { ClearFiltersButton } from '../clear-filters-button/ClearFiltersButton';
import { HighlightsFilters } from '../highlights-filters/HighlightsFilters';
import { NewsList } from '../news-list/NewsList';
import { SideAccordionFilters } from '../side-accordion-filters/SideAccordionFilters';
import styles from './NewsView.module.scss';

export interface NewsViewProps {
  entity?: Entity;
  includedFilterKeys?: NewsFilter['key'][];
  trackingSource: PosthogTrackingSource;
}

export const NewsView = ({
  entity,
  includedFilterKeys,
  trackingSource,
}: NewsViewProps) => {
  const { t } = useTranslation('news');
  const { lists } = useWatchlist();
  const { data: serverFilters = [] } = useNewsFilters();
  const isNewEarningsTabEnabled = useFlags(state =>
    state.getFeatureFlagState('PLATFORM-4671-ao-earnings')
  );
  const isConstrainedLayout = isNewEarningsTabEnabled && !!entity;

  const { trackNewsFilter } = usePosthogEvents();
  const {
    newsMostRead,
    aOMostReadNews,
    filtersMappingFromUrl,
    setMostReadParam,
    setAOMostReadNews,
    setUrlFilters,
    resetAll,
  } = useNewsUrlStore(s => ({
    newsMostRead: s.newsMostRead === '1',
    aOMostReadNews: s.aOMostReadNews === '1',
    filtersMappingFromUrl: s.newsFilters,
    setAOMostReadNews: s.setAOMostReadNews,
    setMostReadParam: s.setMostReadParam,
    setUrlFilters: s.setUrlFilters,
    resetAll: s.resetAll,
  }));

  const baseFilters = createAccordionFilters(serverFilters, lists);
  const filters = applySelectionMapping(
    baseFilters,
    filtersMappingFromUrl || {}
  );

  const newsFilters = includedFilterKeys
    ? filters.filter(f => includedFilterKeys.includes(f.key))
    : filters;

  const initialDataFilters = entity
    ? [
        {
          key: 'entities',
          name: 'Entity',
          options: [
            {
              isChecked: true,
              visible: true,
              key: entity.tag,
              name: entity.name,
            },
          ],
        },
      ]
    : [];
  const newsListFilters = [...initialDataFilters, ...newsFilters];

  const showResetButton =
    !newsMostRead ||
    newsFilters.some(
      f => countFilterKeyChecked({ filters, filterKey: f.key }).some
    );

  return (
    <>
      {entity && (
        <p
          className={clsx(styles.listTitle, {
            [styles.constrained]: isConstrainedLayout,
          })}
        >
          {entity.name} {t('news:title')}
        </p>
      )}
      <div
        className={clsx(styles.newsViewRoot, {
          [styles.constrained]: isConstrainedLayout,
        })}
        data-testid="news-lists-container"
      >
        <div className={styles.leftContent}>
          <NewsList
            filters={newsListFilters}
            isFetchMostRead={entity ? aOMostReadNews : newsMostRead}
            trackingSource={trackingSource}
          />
        </div>
        <div className={styles.rightContent}>
          <HighlightsFilters
            isFetchMostRead={entity ? aOMostReadNews : newsMostRead}
            toggleIsFetchMostRead={isToggled => {
              if (isToggled) {
                trackNewsFilter({
                  source: trackingSource,
                  type: 'most_read',
                  entities: entity ? [entity.tag] : undefined,
                });
              }

              entity
                ? setAOMostReadNews(isToggled)
                : setMostReadParam(isToggled);
            }}
          />
          <SideAccordionFilters
            filters={newsFilters}
            trackingSource={trackingSource}
            entityTag={entity?.tag}
            toggleFilterOption={(filterKey, optionKey, track) => {
              track?.();
              setUrlFilters(
                toggleSelectionMapping({
                  current: filtersMappingFromUrl || {},
                  filters: newsFilters,
                  filterKey,
                  optionKey,
                })
              );
            }}
          />
          {showResetButton && (
            <ClearFiltersButton
              data-testid="reset-all-filters"
              onClick={resetAll}
            />
          )}
        </div>
      </div>
    </>
  );
};
